import {useMemo} from "react";

import {MetricGranularity, MetricSeries, MetricSeriesAnalysis} from "@/lib/types/metricsExplorer.ts";
import {DistributionChart} from "@/pages/metricsExplorer/charts/DistributionChart.tsx";
import {ScatterChart} from "@/pages/metricsExplorer/charts/ScatterChart.tsx";
import {SeasonalityHeatmap} from "@/pages/metricsExplorer/charts/SeasonalityHeatmap.tsx";
import {TimeSeriesChart} from "@/pages/metricsExplorer/charts/TimeSeriesChart.tsx";
import {buildSeriesName} from "@/pages/metricsExplorer/chartSeries.ts";
import {DowntimeWindow} from "@/pages/metricsExplorer/chartShapes.ts";
import {ExplorerChartType} from "@/pages/metricsExplorer/explorerConfig.ts";

interface MetricsExplorerChartProps {
  series: MetricSeries[];
  analysis: MetricSeriesAnalysis[];
  chartType: ExplorerChartType;
  checkNames: Record<number, string>;
  /** Builder row letters by series ref; the dashboard has no rows and leaves this out. */
  seriesLetters?: Record<string, string>;
  granularity: MetricGranularity;
  /** The selected window, which the time axis is held to. */
  start: Date;
  end: Date;
  downtime?: DowntimeWindow[];
}

/**
 * Picks the chart for the selected type.
 *
 * Three of them do not plot against time at all — a scatter pairs two metrics, a
 * distribution bins values, a heatmap reads the seasonality grid — so each owns its whole
 * option set rather than sharing one and switching a series type.
 */
export const MetricsExplorerChart = ({
  series,
  analysis,
  chartType,
  checkNames,
  seriesLetters,
  granularity,
  start,
  end,
  downtime,
}: MetricsExplorerChartProps) => {
  const seriesNames = useMemo(() => {
    const queried = series.filter((item) => item.kind === "PRIMARY");
    const showCheck = new Set(queried.map((item) => item.check_id)).size > 1;
    const showMetric = new Set(queried.map((item) => item.metric)).size > 1;
    return Object.fromEntries(
      series.map((item) => [
        item.key,
        buildSeriesName(item, checkNames, showCheck, showMetric, seriesLetters?.[item.ref]),
      ])
    );
  }, [series, checkNames, seriesLetters]);

  if (chartType === "scatter") {
    return <ScatterChart series={series} />;
  }
  if (chartType === "distribution") {
    return <DistributionChart series={series} seriesNames={seriesNames} />;
  }
  if (chartType === "heatmap") {
    return <SeasonalityHeatmap series={series} analysis={analysis} />;
  }
  return (
    <TimeSeriesChart
      series={series}
      chartType={chartType}
      checkNames={checkNames}
      seriesLetters={seriesLetters}
      granularity={granularity}
      start={start}
      end={end}
      downtime={downtime}
    />
  );
};
